import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LogOut, Loader2, ShieldCheck, KeyRound } from "lucide-react";

const LEN = 4;

export default function PinGate() {
  const { user, ready, verifyPin, setupPin, logout } = useAuth();
  const navigate = useNavigate();
  const setupMode = !!user && !user.has_pin;        // first-time PIN creation
  const [digits, setDigits]   = useState(Array(LEN).fill(""));
  const [confirm, setConfirm] = useState(Array(LEN).fill(""));
  const [stage, setStage]     = useState("enter");  // setup: "enter" → "confirm"; verify: always "enter"
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, [stage]);

  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.pin_verified) return <Navigate to="/" replace />;

  const activeDigits = stage === "confirm" ? confirm : digits;
  const setActive = stage === "confirm" ? setConfirm : setDigits;

  const setDigit = (idx, v) => {
    const ch = (v || "").replace(/\D/g, "").slice(-1);
    setActive((curr) => {
      const next = [...curr];
      next[idx] = ch;
      return next;
    });
    if (ch && idx < LEN - 1) refs.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !activeDigits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter") submit();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, LEN);
    if (!text) return;
    e.preventDefault();
    const next = Array(LEN).fill("");
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setActive(next);
    refs.current[Math.min(text.length, LEN - 1)]?.focus();
  };

  const reset = (which) => {
    if (which === "both") { setDigits(Array(LEN).fill("")); setConfirm(Array(LEN).fill("")); setStage("enter"); }
    else setActive(Array(LEN).fill(""));
    refs.current[0]?.focus();
  };

  const submit = async () => {
    const pin = activeDigits.join("");
    if (pin.length !== LEN) {
      setError(`Enter all ${LEN} digits.`);
      return;
    }
    setError("");

    if (setupMode && stage === "enter") {
      // Advance to confirmation step.
      setStage("confirm");
      return;
    }

    if (setupMode && stage === "confirm") {
      const chosen = digits.join("");
      if (pin !== chosen) {
        setError("PINs don't match. Try again.");
        reset("both");
        return;
      }
      setBusy(true);
      try {
        await setupPin(chosen);
        navigate("/", { replace: true });
      } catch (e) {
        setError(formatApiError(e) || "Couldn't set PIN.");
        reset("both");
      } finally {
        setBusy(false);
      }
      return;
    }

    // Normal verify flow
    setBusy(true);
    try {
      await verifyPin(pin);
      navigate("/", { replace: true });
    } catch (e) {
      setError(formatApiError(e) || "Incorrect PIN.");
      reset("active");
    } finally {
      setBusy(false);
    }
  };

  const onSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const headerCopy = setupMode
    ? {
        eyebrow: "Create your access PIN",
        title: stage === "enter"
          ? <>Welcome, <span className="text-orange-400">{(user.name || "").split(" ")[0] || "team"}</span>.</>
          : <>Confirm your PIN.</>,
        body: stage === "enter"
          ? `Choose a ${LEN}-digit PIN. You'll be asked for it on every fresh sign-in.`
          : "Re-enter the same PIN to confirm.",
        cta: stage === "enter" ? "Continue" : "Save PIN",
        Icon: KeyRound,
      }
    : {
        eyebrow: "Two-step access",
        title: <>Hello, <span className="text-orange-400">{(user.name || "").split(" ")[0] || "team"}</span>.</>,
        body: `Enter your ${LEN}-digit access PIN to continue into the portal.`,
        cta: "Unlock portal",
        Icon: ShieldCheck,
      };
  const { eyebrow, title, body, cta, Icon } = headerCopy;

  return (
    <div data-testid="pin-gate-page" className="min-h-screen bg-slate-950 text-white relative overflow-hidden flex items-center justify-center px-6">
      <div
        className="absolute inset-0 opacity-[0.22]"
        style={{
          background:
            "radial-gradient(50% 60% at 50% 0%, rgba(249,115,22,0.5), transparent 60%), radial-gradient(40% 50% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(60% 70% at 50% 50%, black 50%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(60% 70% at 50% 50%, black 50%, transparent 100%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <img src="/d4ps-logo.png" alt="D4PS" className="h-10 w-10 rounded-sm object-contain bg-black/40 p-1" />
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">D4PS</div>
            <div className="text-[9px] tracking-[0.32em] text-slate-400 mt-0.5">COURIERS &amp; LOGISTICS</div>
          </div>
        </div>

        <div className="text-[11px] uppercase tracking-[0.32em] text-orange-400/95 font-semibold inline-flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-orange-500" />
          {eyebrow}
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight">
          {title}
        </h1>
        <div className="mt-3 h-[3px] w-12 bg-orange-500" />
        <p className="mt-4 text-slate-300 text-sm">{body}</p>

        <div className="mt-8 flex items-center gap-3" onPaste={onPaste}>
          {activeDigits.map((d, i) => (
            <input
              key={`${stage}-${i}`}
              ref={(el) => (refs.current[i] = el)}
              data-testid={`pin-digit-${i}`}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              className="h-14 w-14 rounded-sm bg-slate-900/70 border border-slate-700 text-white text-center font-display text-2xl tabular-nums tracking-widest focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500"
            />
          ))}
        </div>

        {error && (
          <div data-testid="pin-error" className="mt-4 text-sm text-rose-300 bg-rose-950/40 border border-rose-900/60 px-3 py-2 rounded-sm">
            {error}
          </div>
        )}

        <Button
          data-testid="pin-submit"
          onClick={submit}
          disabled={busy}
          className="mt-6 w-full h-11 rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-medium"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          {cta}
        </Button>

        {setupMode && stage === "confirm" && (
          <button
            type="button"
            onClick={() => reset("both")}
            className="mt-3 text-xs text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline"
            data-testid="pin-restart"
          >
            Start over
          </button>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-between text-[11px] text-slate-500">
          <span>{user.email}</span>
          <button onClick={onSignOut} className="inline-flex items-center gap-1 hover:text-slate-300" data-testid="pin-signout">
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
